// web/src/types/roast.ts

// --- AudioProfile + AudioAssets (re-exported for convenience; canonical source is @/player/audio/types) ---
export type { AudioProfile, AudioAssets, MusicMood, SoundEffectCategory, SoundEffectTiming, ContextualTag, SoundEffectOpportunity } from "@/player/audio/types";
import type { AudioAssets } from "@/player/audio/types";
import type { CaptionMask, BleepMode } from "@/config/censorship";

// --- Shared subset: byte-identical to functions/src/lib/types.ts ---
export type Mode = "roast" | "toast";
export type Profanity = "clean" | "medium" | "savage";

export interface WordTiming { word: string; start: number; end: number } // seconds

export interface DetectedObject {
  label: string;
  box_2d: [number, number, number, number]; // [ymin,xmin,ymax,xmax] 0-1000
  confidence: number;                        // 0.0-1.0
  dialogue_reference: string;                // exact continuous quote from script
  start: number;                             // seconds (0 until aligned)
  end: number;                               // seconds (0 until aligned)
}

export type SourceType = "photo" | "deck" | "doc";

/** A key slide the AI dwells on: crisp raster + critique regions (box_2d). */
export interface KeySlide {
  slideIndex: number;           // page number in the deck (0-based)
  imageUrl: string;             // crisp/high-DPI, persisted
  imagePath: string;            // Storage path (for copy-at-save + delete)
  detectedObjects: DetectedObject[]; // same shape as photo roast
  doodleImageUrl?: string;           // comedic doodle overlay (revealed a beat into dwell); absent = no doodle
  doodlePath?: string;               // Storage path for the doodle image
}

/** Per-key-slide critique regions, keyed by slide index (mirrors functions/src/lib/types.ts). */
export type KeySlideRegions = Record<number, DetectedObject[]>;

/** A low-res filler frame shown only during a riffle sweep. */
export interface FillerImage {
  slideIndex: number;
  imageUrl: string;
  imagePath: string;
}

/** One entry in the deck flip-through schedule. */
export interface SlideScheduleEntry {
  slideIndex: number;
  startSec: number;
  endSec: number;
  kind: "dwell" | "riffle";
  zoomDisabled?: boolean;       // true when timings interpolated → hold full slide
  startWord?: number;           // half-open [startWord, endWord); present ⇒ word-aligned schedule
  endWord?: number;
}

/** The closing verdict rendered as a card / share thumbnail. */
export interface DeckVerdict { grade?: string; line: string }

/** One narration segment: tentpole → a single slideIndex; sweep → a contiguous run. */
export interface Segment { slideIndices: number[]; text: string; zoomPhrase?: string }

/** Realized clip timing for a segment, in concat order. clipEndSec[i] === clipStartSec[i+1]
 *  (inter-clip gaps folded into the preceding clip). The client builds the schedule from these. */
export interface SegmentTiming { slideIndices: number[]; clipStartSec: number; clipEndSec: number }

// --- Doodle callable types (generateDoodle) ---
export interface DoodleRequest {
  image: string;       // base64-encoded image bytes
  mimeType: string;    // e.g. "image/jpeg"
  mode: Mode;
  script?: string;     // full roast script, threaded into doodle prompt when provided
}

export interface DoodleResponse {
  imageBase64: string;
  mimeType: string;
}

// --- Web-only types ---
export type RoastStatus = "processing" | "ready" | "failed" | "hidden";
export type AspectRatio = "16:9" | "9:16";

export interface CensorRegion { start: number; end: number; word: string } // seconds

export interface RoastArtifact {
  id: string;
  ownerUid: string;
  ownerEmail: string;
  createdAt: number;
  updatedAt: number;
  status: RoastStatus;
  visibility: "private" | "unlisted";
  mode: Mode;
  profanity: Profanity;
  title: string;
  script: string;
  scriptOriginal: string;
  name: string | null;
  context: string | null;
  imagePath: string | null;
  imageUrl: string | null;
  audioPath: string | null;
  audioUrl: string | null;
  musicTrack: string | null; // persisted mirror of audio.musicTrack (the keyword-selected track the player actually plays via audio.musicTrack)
  durationSec: number;
  wordTimings: WordTiming[];
  detectedObjects: DetectedObject[];
  censorRegions: CensorRegion[];
  voiceName: string;
  isCharacterFullscreen: boolean;
  aspect: AspectRatio;
  viewCount: number;
  // Opt-in censorship flags (optional — omitted/undefined ⇒ today's behavior, so old
  // saved roasts replay identically. See @/config/censorship for the defaults.)
  captionMask?: CaptionMask;  // caption redaction style; default "vowels"
  bleepMode?: BleepMode;      // spoken-audio mode; default "tone"
  // Doodle fields (optional — omitted/undefined when no doodle was generated).
  // doodleRevealTime is derivable from wordTimings; not persisted.
  doodleImagePath?: string | null;
  doodleImageUrl?: string | null;
  // Resolved audio assets (music track + SFX cues + source profile).
  audio: AudioAssets;
  // ---- deck source (sourceType === "deck"); absent/"photo" = legacy photo roast ----
  sourceType?: SourceType;
  deckPdfPath?: string | null;
  keySlides?: KeySlide[];
  fillerImages?: FillerImage[];
  slideSchedule?: SlideScheduleEntry[];
  verdict?: DeckVerdict | null;
}

/** A roast is a deck iff sourceType === "deck". Missing → legacy photo. */
export function isDeck(a: Pick<RoastArtifact, "sourceType">): boolean {
  return a.sourceType === "deck";
}

/** Paged sources (deck or doc) use the slide/page player path. */
export function isPaged(a: Pick<RoastArtifact, "sourceType">): boolean {
  return a.sourceType === "deck" || a.sourceType === "doc";
}
